import { useMemo, useState } from "react";
import { LoadingWrapper, PaginatedReactTable, SafeButton } from "../common";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button, Icon, Message, Popup } from "semantic-ui-react";
import { statusMap } from "../common/helpers";
import { canEditAssets, canEditLimited, fetchWithAuth, getCSRFToken, isAssociateManagerOrAbove, isICCSupport } from "../auth";
import { usePaginatedList } from "../../utils/usePaginatedList";
import "../../css/members.css"

const transformAsset = (asset) => ({
  ...asset,
  associatedInfo:
    asset.associatedTeam !== undefined
      ? { name: asset.associatedTeam.name, link: "/teams/" + asset.associatedTeam.id }
      : asset.assignedMember !== undefined
      ? { name: asset.assignedMember.name, link: "/members/" + asset.assignedMember.userId }
      : { name: "Available" },
});

export const Assets = (props) => {
  const [selectedRows, setSelectedRows] = useState({})
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If a user comes from "Forms > Asset Problems" this will be set.
  // It will hide all interactions with assets and change routing
  // when clicking on asset header to go directly to problem creation.
  const routesToProblem = searchParams.get("problem") === "true";

  const {
    results,
    count,
    loading,
    page,
    pageSize,
    filters,
    ordering,
    setPage,
    setPageSize,
    setFilter,
    setOrdering,
    refetch,
  } = usePaginatedList('assets/');

  const transformedAssets = useMemo(() => results.map(transformAsset), [results]);


  const exportAssets = async () => {
    setError("")
    const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assetsexport/', {
      headers: { 'X-CSRFToken': getCSRFToken() }
    })
    if (!resp.ok) {
      setError("Something went wrong when exporting assets.")
      return
    }
    const respBlob = await resp.blob()
    const url = window.URL.createObjectURL(respBlob)
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Assets.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function deleteSelectedAssets() {
    setError('')
    setSuccess('')
    let errored = []
    async function deleteAssetsPost(assetId, assetName) {
      const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `assets/${assetId}/`,
        {
          method: "DELETE",
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      )
      if (resp.status > 399) {
        errored.push(assetName)
      }
    }
    for (const assetIndex in selectedRows) {
      try {
        const toDelete = selectedRows[assetIndex]
        if (toDelete) {
          const assetId = transformedAssets[assetIndex].id
          const assetName = transformedAssets[assetIndex].iccName
          await deleteAssetsPost(assetId, assetName)
        }
      } catch { }  // some deleted asset from earlier still in the state
    }
    if (errored.length > 0) {
      setError("Failed to delete: " + errored)
    } else {
      setSuccess("Successfully deleted assets.")
    }
    setSelectedRows({})
    refetch()
  }

  async function markSelectedRequiredForReadiness() {
    setError('')
    setSuccess('')

    // Collect all selected asset IDs
    const assetIds = []
    for (const assetIndex in selectedRows) {
      try {
        const toUpdate = selectedRows[assetIndex]
        if (toUpdate) {
          const assetId = transformedAssets[assetIndex].id
          assetIds.push(assetId)
        }
      } catch (e) {
        // Handle cases where asset index is invalid
        console.error('Error processing asset:', e)
      }
    }

    if (assetIds.length === 0) {
      setError('No assets selected')
      return
    }

    try {
      const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assets/required-for-readiness/', {
        method: "POST",
        headers: {
          'X-CSRFToken': getCSRFToken(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(assetIds)
      })

      if (resp.ok) {
        setSuccess(`Successfully marked ${assetIds.length} asset(s) as required for readiness.`)
      } else {
        const errorData = await resp.json().catch(() => ({}))
        const errorMessage = errorData.error || errorData.detail || 'Failed to update assets as required for readiness'
        setError(errorMessage)
      }
    } catch (e) {
      console.error('Error updating assets:', e)
      setError('Failed to update assets as required for readiness')
    }

    setSelectedRows({})
    refetch()
  }

  const columnInfo = useMemo(() => [
      {
        Header: "ID",
        accessor: "id"
      },
      {
        Header: "ICC Name",
        accessor: "iccName",
        serverFilterParam: "iccName",
        Cell: (row) => {
          const lastUpdated = new Date(row.row.original.checklistVerifiedOn)
          const midnight = new Date()
          midnight.setHours(0, 0, 0, 0)
          const needsVerification = lastUpdated < midnight
          return <>
            <Link to={"/assets/" + row.row.original.id + (routesToProblem ? "/problems/new" : "/view")}>{row.row.original.iccName}</Link>
            {row.row.original.deviceSubType.checklistExists && needsVerification &&
              <Popup content="This asset has not had it's checklist verified yet today."
                trigger={<Icon style={{ marginLeft: "0.3rem" }}
                  className="stopwatch"
                  color="red" />} />}
          </>
        }
      },
      {
        Header: "Make",
        accessor: "make",
        serverFilterParam: "make",
        Cell: (row) => {
          return <div>{row.row.original.make}</div>
        }
      },
      {
        Header: "Model",
        accessor: "model",
        serverFilterParam: "model",
        Cell: (row) => {
          return <div>{row.row.original.model}</div>
        }
      },
      {
        Header: "Year",
        accessor: "year",
        serverFilterParam: "year",
        Cell: (row) => {
          return <div>{row.row.original.year}</div>
        }
      },
      {
        Header: "VIN",
        accessor: "vin",
        serverFilterParam: "vin",
        Cell: (row) => {
          return <div>{row.row.original.vin}</div>
        }
      },
      {
        Header: "Device Type",
        accessor: "deviceSubType.deviceType.name",
        serverFilterParam: "DeviceTypeName",
        serverSortParam: "DeviceTypeName",
        Cell: (row) => {
          return <div>{row.row.original.deviceSubType.deviceType.name}</div>
        }
      },
      {
        Header: "Device Sub Type",
        accessor: "deviceSubType.name",
        serverFilterParam: "deviceSubTypeName",
        serverSortParam: "deviceSubTypeName",
        Cell: (row) => {
          return <div>{row.row.original.deviceSubType.name}</div>
        }
      },
      {
        Header: "Status",
        accessor: "status",
        serverFilterParam: "status",
        Cell: (row) => {
          const displayValue = statusMap[row.row.original.status]
          return <div>{displayValue ? displayValue : row.row.original.status}</div>
        }
      },
      {
        Header: "Team/Member",
        accessor: "associatedInfo.name",
        serverFilterParam: "teamName",
        serverSortParam: "teamName",
        Cell: (row) => {
          if (row.row.original.associatedInfo.name !== "Available") {
            return <Link to={row.row.original.associatedInfo.link}>{row.row.original.associatedInfo.name}</Link>
          } else {
            return <div style={{ color: "darkgray" }}>Available</div>
          }
        }
      }
    ], []);

  function canMarkRequiredForReadiness() {
    return isAssociateManagerOrAbove() || isICCSupport();
  }

  return (
    <div>
      {error !== '' &&
        <Message style={{ width: "fit-content" }} compact negative>{error}</Message>
      }
      {success !== '' &&
        <Message style={{ width: "fit-content" }} compact positive>{success}</Message>
      }
      <LoadingWrapper text="Loading assets" loading={loading && results.length === 0}>
        <PaginatedReactTable
          className="channel-table"
          data={transformedAssets}
          columns={columnInfo}
          hiddenColumns={['id']}
          editable={!routesToProblem && (canEditAssets() || canEditLimited() || canMarkRequiredForReadiness())}
          onSelectionChange={setSelectedRows}
          count={count}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSortChange={setOrdering}
          onFilterChange={setFilter}
          initialFilters={filters}
          initialOrdering={ordering}
          loading={loading && results.length > 0}
        />
        {!routesToProblem && (canEditAssets() || canMarkRequiredForReadiness()) && (
          <div style={{ paddingBottom: "1rem", paddingTop: "1rem" }}>
            {canEditAssets() && (
              <Button style={{ backgroundColor: "var(--add)", color: "white" }} onClick={() => navigate('/assets/newasset/view')}>Add asset</Button>
            )}
            {canMarkRequiredForReadiness() && (
              <SafeButton
                style={{ backgroundColor: "var(--warning)", color: "white", marginLeft: "0.5rem" }}
                submitText="Mark selected required for readiness"
                submitAction={markSelectedRequiredForReadiness}
              />
            )}
            {canEditAssets() && (
              <SafeButton
                style={{ backgroundColor: "var(--delete)", color: "white", marginLeft: "0.5rem" }}
                submitText="Delete selected"
                submitAction={deleteSelectedAssets}
              />
            )}
          </div>
        )}
        {!routesToProblem && (
          <Button onClick={exportAssets}><Icon className="download" />Export</Button>
        )}
      </LoadingWrapper>
    </div>
  );
}
